import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "../../../api-client";
import type {
	AdminFeedbackFilters,
	AdminUpdateFeedback,
	CreateFeedback,
} from "../../../schemas/feedback";
import { queryKeys } from "../../query-keys";

export function useSubmitFeedback() {
	return useMutation({
		mutationFn: (body: CreateFeedback) => adminApi.feedback.submit(body),
	});
}

export function useUploadFeedbackScreenshot() {
	return useMutation({
		mutationFn: (file: File) => adminApi.feedback.uploadScreenshot(file),
	});
}

export function useAdminFeedbackList(filters?: AdminFeedbackFilters) {
	return useQuery({
		queryKey: queryKeys.feedback.list(filters as Record<string, unknown>),
		queryFn: () => adminApi.feedback.adminList(filters),
		placeholderData: keepPreviousData,
	});
}

export function useAdminFeedback(id: string | undefined) {
	return useQuery({
		queryKey: id ? queryKeys.feedback.detail(id) : queryKeys.feedback.all,
		queryFn: () => {
			if (!id) throw new Error("feedback id required");
			return adminApi.feedback.adminGet(id);
		},
		enabled: !!id,
	});
}

export function useUpdateFeedback() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: AdminUpdateFeedback }) =>
			adminApi.feedback.adminUpdate(id, body),
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.feedback.all });
			queryClient.invalidateQueries({
				queryKey: queryKeys.feedback.detail(id),
			});
		},
	});
}
